import { createElement, useRef, useState } from "react";
import { ListField } from "@/components/admin/website/ListField";
import axios from "axios";
import { toast } from "sonner";
import { FolderOpen, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/RichTextEditor";
import { MediaPicker } from "@/components/admin/MediaLibrary";

export const move = (arr, i, d) => {
  const j = i + d;
  if (j < 0 || j >= arr.length) return arr;
  const a = [...arr];
  [a[i], a[j]] = [a[j], a[i]];
  return a;
};

export const MediaField = ({ value, onChange, testId }) => {
  const { token } = useAuth();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(false);
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/cms/media`, fd, { headers: { Authorization: `Bearer ${token}` } });
      onChange(r.data.media.url);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  const isImg = /\.(png|jpe?g|webp|gif)(\?|$)/i.test(value || "") || (value || "").includes("image");
  return (
    <div className="flex items-center gap-2">
      {value && isImg && <img src={value} alt="" className="w-10 h-10 rounded object-cover border" />}
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://... or upload" className="flex-1" data-testid={testId} />
      <input ref={ref} type="file" className="hidden" accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.rtf,.odt,.ods,.odp,.zip" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
      <Button type="button" size="icon" variant="outline" onClick={() => ref.current.click()} disabled={busy} title="Upload" data-testid={`${testId}-upload`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}</Button>
      <Button type="button" size="icon" variant="outline" onClick={() => setPick(true)} title="Media library" data-testid={`${testId}-library`}><FolderOpen className="w-4 h-4" /></Button>
      <MediaPicker open={pick} onOpenChange={setPick} onSelect={(m) => { onChange(m.url); setPick(false); }} />
    </div>
  );
};

export const ButtonsField = ({ value = [], onChange, testId }) => (
  <div className="space-y-2">
    {value.map((b, i) => (
      <div key={i} className="flex gap-2">
        <Input value={b.label} placeholder="Label" onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} data-testid={`${testId}-${i}-label`} />
        <Input value={b.href} placeholder="/book-trial or https://" onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, href: e.target.value } : x)))} data-testid={`${testId}-${i}-href`} />
        <Button type="button" size="icon" variant="ghost" onClick={() => onChange(value.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
      </div>
    ))}
    {value.length < 3 && <Button type="button" size="sm" variant="outline" onClick={() => onChange([...value, { label: "", href: "" }])} data-testid={`${testId}-add`}><Plus className="w-3.5 h-3.5 mr-1" />Add button</Button>}
  </div>
);

export const Field = ({ f, value, onChange, testId, renderColumns }) => {
  const t = `${testId}-${f.key}`;
  switch (f.type) {
    case "richtext": return <RichTextEditor value={value || ""} onChange={onChange} minHeight={70} testId={t} />;
    case "textarea": return <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={t} />;
    case "media": return <MediaField value={value} onChange={onChange} testId={t} />;
    case "buttons": return <ButtonsField value={value} onChange={onChange} testId={t} />;
    case "checkbox": return <input type="checkbox" checked={value !== false} onChange={(e) => onChange(e.target.checked)} data-testid={t} />;
    case "number": return <Input type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))} data-testid={t} />;
    case "select": return (
      <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={value ?? ""} onChange={(e) => onChange(typeof f.options[0] === "number" ? Number(e.target.value) : e.target.value)} data-testid={t}>
        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
    case "list": return createElement(ListField, { f, value: value || [], onChange, testId: t });
    case "columns": return renderColumns(value || [], onChange, t);
    default: return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={t} />;
  }
};

export const FieldGrid = ({ fields, data, onChange, testId, renderColumns }) => (
  <div className="space-y-3">
    {fields.map((f) => {
      // Composite fields contain buttons; a <label> wrapper would forward clicks to the first button inside (e.g. "delete column")
      const Wrap = ["richtext", "media", "buttons", "list", "columns"].includes(f.type) ? "div" : "label";
      return (
        <Wrap key={f.key} className="block">
          <span className="text-xs font-semibold text-slate-600">{f.label}</span>
          <div className="mt-1"><Field f={f} value={data[f.key]} onChange={(v) => onChange({ ...data, [f.key]: v })} testId={testId} renderColumns={renderColumns} /></div>
        </Wrap>
      );
    })}
  </div>
);
